import { useEffect, useRef, useState } from "react";
import { Flex, Text, Pill } from "@theorgchartdev/storybook";
import { appstr } from "@coreapp/utils/appstr";
import { ReadOnlyCriteriaList } from "./ReadOnlyCriteriaList";
import { useRightSidebarContext } from "@/providers/RightSidebarProvider";
import { usePermissionCheck } from "@/hooks/usePermissionCheck";
import { ShareFilterModal } from "@/appComponents/Blocks/Modals/ShareFilterModal/ShareFilterModal";
import { QRulePair } from "@coreapp/CoreApp/QRulePair";
import type { SharedFilterDoc } from "../FilterPanel/useSharedFilterController";
import { RSBFooter, RSBPrimaryButton, RSBSecondaryButton } from "./RSBFooter";
import { ConfirmDeleteButton } from "./ConfirmDeleteButton";
import { chartModeTestId } from "./chartModeTestId";

type ShareProps = {
  definition: any;
  filterRef: any;
  sharedDoc?: SharedFilterDoc;
  onShareComplete?: () => void;
};

type FilterDetailsViewProps = {
  entityType: "filter" | "highlight";
  name: string;
  ownerDisplayName: string;
  isShared: boolean;
  userRole?: SharedFilterDoc["userRole"];
  rules: QRulePair[];
  onBack: () => void;
  shareProps?: ShareProps;
  onEdit?: () => void;
  onCopy?: () => void;
  onDelete?: () => void;
};

export const FilterDetailsView = ({
  entityType,
  name,
  ownerDisplayName,
  isShared,
  userRole,
  rules,
  onBack,
  shareProps,
  onEdit,
  onCopy,
  onDelete
}: FilterDetailsViewProps) => {
  const isFilter = entityType === "filter";
  const modeTestId = (suffix: string) => chartModeTestId(isFilter ? "filter" : "highlight", suffix);
  const entityLabel = appstr(isFilter ? "Filter" : "Highlight");
  const { setCustomHeaderProps, closeSidebar } = useRightSidebarContext();
  const { canShareFilters: canShare, canEditDocuments } = usePermissionCheck();
  const [showShareModal, setShowShareModal] = useState(false);
  const [localIsShared, setLocalIsShared] = useState(isShared);
  const onBackRef = useRef(onBack);
  onBackRef.current = onBack;
  useEffect(() => {
    setLocalIsShared(isShared);
  }, [isShared]);
  const showShareButton = canShare && shareProps;
  const isOwner = userRole !== undefined && userRole === "owner";
  const canEdit = isOwner || !userRole || (canEditDocuments && userRole !== "viewer");

  useEffect(() => {
    setCustomHeaderProps(() => ({
      iconProps: {
        size: "xsmall",
        onClick: () => {
          setCustomHeaderProps({});
          onBackRef.current();
        },
        dataTestId: modeTestId("details-back-btn")
      },
      icon: "chevron-left",
      title: appstr(entityType === "filter" ? "Back to filters" : "Back to highlights"),
      ...(showShareButton
        ? {
            headerActions: {
              closeAction: closeSidebar,
              closeActionProps: {
                dataTestId: modeTestId("details-close-btn")
              },
              customAction: {
                icon: "share-nodes",
                onClick: () => setShowShareModal(true),
                buttonProps: {
                  modifier: "tertiary",
                  tooltipProps: {
                    message: appstr(entityType === "filter" ? "Share filter" : "Share highlight")
                  },
                  dataTestId: modeTestId("details-share-btn")
                }
              }
            }
          }
        : {})
    }));

    return () => {
      setCustomHeaderProps({});
    };
  }, [showShareButton]);

  const confirmMessage = localIsShared
    ? appstr(isFilter ? "Delete shared filter confirm" : "Delete shared highlight confirm")
    : appstr(isFilter ? "Delete filter confirm" : "Delete highlight confirm");

  const deleteButton = onDelete ? (
    <ConfirmDeleteButton
      onSubmit={onDelete}
      tooltipMessage={appstr("Delete")}
      headerTitle={appstr(isFilter ? "Delete filter" : "Delete highlight")}
      triggerTestId="details-view-delete-btn"
    >
      {confirmMessage}
    </ConfirmDeleteButton>
  ) : null;

  return (
    <>
      <Flex direction="column" height="100%" gap="12px" padding="16px" overflow="auto">
        <Flex direction="column" gap="4px">
          <Text size="small" modifier="medium">
            {appstr("Owner")}
          </Text>
          <Text size="small">{ownerDisplayName}</Text>
        </Flex>
        <Flex direction="column" gap="4px">
          <Text size="small" modifier="medium">
            {appstr("Entity name", { entity: entityLabel })}
          </Text>
          <Flex gap="8px" alignItems="center">
            <Text size="small">{name}</Text>
            {localIsShared && <Pill label={appstr("Shared")} color="info" size="compact" />}
          </Flex>
        </Flex>
        {rules.length > 0 && (
          <Flex direction="column" gap="4px">
            <Text size="small" modifier="medium">
              {appstr("Criteria")}
            </Text>
            <div className="ocn-list-scrollbar">
              <ReadOnlyCriteriaList rules={rules} />
            </div>
          </Flex>
        )}
      </Flex>
      <RSBFooter leftSlot={deleteButton}>
        {onCopy && (
          <RSBSecondaryButton
            iconProps={{ name: "copy" }}
            onClick={onCopy}
            modifier="secondary"
            dataTestId={modeTestId("details-copy-btn")}
          >
            {isOwner
              ? appstr(isFilter ? "Copy filter" : "Copy highlight")
              : appstr(isFilter ? "Copy to My filters" : "Copy to My highlights")}
          </RSBSecondaryButton>
        )}
        {onEdit && canEdit && (
          <RSBPrimaryButton
            iconProps={{ name: "pen-to-square" }}
            onClick={onEdit}
            dataTestId={modeTestId("details-edit-btn")}
          >
            {appstr(isFilter ? "Edit filter" : "Edit highlight")}
          </RSBPrimaryButton>
        )}
      </RSBFooter>
      {showShareButton && (
        <ShareFilterModal
          show={showShareModal}
          onClose={() => setShowShareModal(false)}
          filterType={entityType}
          definition={shareProps.definition}
          filterName={name}
          filterRef={shareProps.filterRef}
          onShareComplete={() => {
            setLocalIsShared(true);
            shareProps.onShareComplete?.();
          }}
          {...(shareProps.sharedDoc
            ? {
                editMode: true,
                existingFilterId: shareProps.sharedDoc._id,
                existingSharedWith: shareProps.sharedDoc.sharedWith
              }
            : {})}
        />
      )}
    </>
  );
};
